import { Hero as HeroSection } from 'components/Hero';
import Layout from 'components/Layout';
import { ProductCardGrid } from 'components/ProductCardGrid';
import { SEO } from 'components/SEO';
import { graphql } from 'gatsby';
import React, { lazy, LazyExoticComponent, useEffect } from 'react';
import track from 'utils/analytics/track';
import { ANALYTICS } from 'utils/constants/analytics';
import { IContentWithImage } from 'utils/types';

const FullBackgroundSection = lazy(
  () => import('components/FullBackgroundSection'),
);
const OrganizationCardsSection = lazy(
  () => import('containers/LifeSciencePage/OrganizationCardsSection'),
);
const MembersSatisfaction = lazy(
  () => import('containers/HealthPlansPage/MembersSatisfaction'),
);
const HowItWorksSection = lazy(
  () => import('components/EverlyCarePage/HowItWorks'),
);
const ImageSection = lazy(() => import('components/ImageSection'));
const ProductCardGridSection = lazy(() => import('components/ProductCardGrid'));
const StepCardListSection = lazy(() => import('components/StepCardList'));

type Props = {
  data: {
    lifeScienceContent: {
      title: string;
      heroSection: {
        headline: string;
        backgroundColor: string;
        desktopImage: {
          url: string;
          title: string;
        };
        cta: {
          text: string;
          url: string;
        };
      };
      sections: {
        sectionImage: {
          url: string;
        };
        componentName: string;
        headline: string;
        disclaimer?: {
          disclaimer: string;
        };
        description: {
          description: string;
          childMarkdownRemark: {
            rawMarkdownBody: string;
          };
        };
        slug: string;
        title?: string;
        listOfContentWithImages?: IContentWithImage[];
        container?: any;
      }[];
    };
  };
};

const components: {
  HeroSection: React.FC<any>;
  MembersSatisfaction: LazyExoticComponent<React.FC<any>>;
  FullBackgroundSection: LazyExoticComponent<React.FC<any>>;
  HowItWorksSection: LazyExoticComponent<React.FC<any>>;
  ProductCardGrid: LazyExoticComponent<React.FC<any>>;
  StepCardList: LazyExoticComponent<React.FC<any>>;
  ImageSection: LazyExoticComponent<React.FC<any>>;
  OrganizationCardsSection: LazyExoticComponent<React.FC<any>>;
} = {
  HeroSection,
  MembersSatisfaction,
  FullBackgroundSection: FullBackgroundSection,
  HowItWorksSection: HowItWorksSection,
  ProductCardGrid: ProductCardGridSection,
  StepCardList: StepCardListSection,
  ImageSection: ImageSection,
  OrganizationCardsSection: OrganizationCardsSection,
};

const isProductCardGrid = (name: string): boolean => name === 'ProductCardGrid';
const isStepCardList = (name: string): boolean => name === 'StepCardList';

export const Head = () => <SEO />;

const LifeSciencePage: React.FC<Props> = ({ data }) => {
  // @ts-ignore
  const { hero, sections } = data.contentfulPage001;

  useEffect(() => {
    track({
      event: ANALYTICS.EVENTS.VIEWED_EHS_PAGE,
      data: {
        label: 'EHS Life Sciences page',
      },
    });
  }, []);

  return (
    <Layout>
      <HeroSection {...hero} />

      {sections.map(
        (
          section: Props['data']['lifeScienceContent']['sections'][number],
          i: number,
        ) => {
          const Component =
            components[section.componentName as keyof typeof components];

          if (!Component) return null;

          if (isProductCardGrid(section.componentName)) {
            return (
              <ProductCardGrid
                key={`${section.componentName}-${i}`}
                {...section}
                title={section.title ?? ''}
                cards={section.container}
                sectionHeader={{
                  ...section,
                  eyebrow: section.title || '',
                  title: section.headline || '',
                  description:
                    section.description?.childMarkdownRemark?.rawMarkdownBody,
                }}
                showOverlay={false}
                cardsPerRow={3}
              />
            );
          }

          if (isStepCardList(section.componentName)) {
            return (
              <StepCardListSection
                key={`${section.componentName}-${i}`}
                {...section}
                listOfContentWithImages={section.listOfContentWithImages ?? []}
                eyebrow={section.title || ''}
                heading={section.headline}
                description={section.description}
              />
            );
          }

          return (
            // @ts-ignore
            <Component key={`${section.componentName}-${i}`} {...section} />
          );
        },
      )}
    </Layout>
  );
};

export default LifeSciencePage;

export const data = graphql`
  query LifeSciencePageQuery {
    contentfulPage001(displayName: { eq: "Life Sciences | EHS" }) {
      slug
      hero {
        ... on ContentfulHeroSection {
          eyebrow
          eyebrowColor
          headline
          description {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          backgroundColor
          desktopImage {
            url
            title
          }
          cta {
            text
            url
          }
        }
      }
      sections {
        headline
        title
        subHeadline
        componentName
        slug
        backgroundColor
        description {
          description
        }
        disclaimer {
          childMarkdownRemark {
            html
            rawMarkdownBody
          }
        }
        sectionImage {
          gatsbyImageData(placeholder: BLURRED, quality: 75)
          url
        }
        cta {
          text
          url
        }
        listOfContentWithImages {
          ... on ContentfulContentWithImage {
            backgroundColor
            heading
            description {
              childMarkdownRemark {
                rawMarkdownBody
                html
              }
            }
            image {
              title
              url
            }
            cta {
              text
              url
            }
          }
        }
        container {
          ... on ContentfulComparisonTable {
            memberA {
              title
              subtitle
              image {
                description
                gatsbyImageData(layout: CONSTRAINED, width: 400, quality: 80)
              }
            }
            memberB {
              title
              subtitle
              image {
                description
                gatsbyImageData(layout: CONSTRAINED, width: 400, quality: 80)
              }
            }
            rows {
              id
              label
              memberAContent {
                memberAContent
              }
              memberBContent {
                memberBContent
              }
              colorMemberA
              colorMemberB
            }
          }
          ... on ContentfulSlider {
            slides {
              slideHeading: heading
              description {
                childMarkdownRemark {
                  rawMarkdownBody
                }
              }
              backgroundImage {
                file {
                  url
                }
              }
              mobileBackgroundImage {
                file {
                  url
                }
              }
              ctaButton {
                url
                label
              }
              features {
                icon {
                  url
                }
                text
              }
              navigationLabel
            }
          }
          ... on ContentfulHealthSolutionCard {
            icon {
              url
            }
            title
            description
            link
          }
        }
      }
    }
  }
`;
